import React, { useState, useEffect } from 'react';
import api from '../api';

const emptyRule = {
  name: '',
  type: 'percentage',
  value: '',
  min_purchase: '0',
  applicable_to: 'all',
  status: 'active',
};

const Discounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyRule);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/discounts/'),
      api.get('/discount-rules/'),
    ])
      .then(([dRes, rRes]) => {
        setDiscounts(Array.isArray(dRes.data) ? dRes.data : dRes.data.results || []);
        setRules(Array.isArray(rRes.data) ? rRes.data : rRes.data.results || []);
      })
      .catch(() => setError('Failed to load discounts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/discount-rules/', {
        ...form,
        value: Number(form.value),
        min_purchase: Number(form.min_purchase || 0),
      });
      setShowModal(false);
      setForm(emptyRule);
      load();
    } catch {
      setError('Failed to create discount rule');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (rule) => {
    try {
      await api.patch(`/discount-rules/${rule.id}/`, {
        status: rule.status === 'active' ? 'inactive' : 'active',
      });
      load();
    } catch {
      setError('Failed to update rule');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="flex-between">
        <h2>Discounts</h2>
      </div>
      {error && <div className="error">{error}</div>}
      <table className="table">
        <thead>
          <tr>
            <th>Member</th>
            <th>Amount</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {discounts.map(d => (
            <tr key={d.id}>
              <td>{d.member_name}</td>
              <td>{Number(d.amount).toLocaleString()}</td>
              <td>{d.date}</td>
            </tr>
          ))}
          {discounts.length === 0 && (
            <tr><td colSpan={3}>No distributed discounts yet — run Aggregate → Distribute</td></tr>
          )}
        </tbody>
      </table>

      <div className="flex-between" style={{ marginTop: 30 }}>
        <h2>Discount Rules</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>New Rule</button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Value</th>
            <th>Min Purchase</th>
            <th>Applicable To</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rules.map(r => (
            <tr key={r.id}>
              <td>{r.name}</td>
              <td>{r.type}</td>
              <td>{Number(r.value).toLocaleString()}</td>
              <td>{Number(r.min_purchase).toLocaleString()}</td>
              <td>{r.applicable_to || 'all'}</td>
              <td><span className={`badge ${r.status}`}>{r.status}</span></td>
              <td>
                <button className="btn btn-outline" onClick={() => toggleStatus(r)}>
                  {r.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>New Discount Rule</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Value</label>
                  <input type="number" step="0.01" min="0" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>Min Purchase</label>
                <input type="number" step="0.01" min="0" value={form.min_purchase} onChange={e => setForm({ ...form, min_purchase: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Applicable To</label>
                <input
                  placeholder="all or supplier category e.g. Electronics"
                  value={form.applicable_to}
                  onChange={e => setForm({ ...form, applicable_to: e.target.value })}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discounts;
